import { useState, useEffect, useRef, useCallback } from "react";
import { C } from "../data";
import Img from "./Img";
import Footer from "./Footer";
import ProductReviews, { Stars } from "./ProductReviews";
import { fetchReviewStats } from "../api";
import { useCart } from "../context/CartContext";

function ReviewSummaryBadge({ productId }) {
  const [stats, setStats] = useState(null);
  useEffect(() => { fetchReviewStats(productId).then(setStats).catch(() => {}); }, [productId]);
  if (!stats || stats.total_count === 0) return null;
  return (
    <button
      onClick={() => document.getElementById("product-reviews")?.scrollIntoView({ behavior: "smooth" })}
      style={{ display: "flex", alignItems: "center", gap: 8, background: "none", border: "none", cursor: "pointer", padding: 0, marginBottom: 16 }}
    >
      <Stars value={stats.avg_rating} size={13} />
      <span style={{ fontFamily: "'Presentation5',sans-serif", fontWeight: 300, fontSize: 11, color: C.muted, letterSpacing: .5 }}>
        {stats.avg_rating} ({stats.total_count})
      </span>
    </button>
  );
}

export default function DetailPage({ product, allProducts, onGoDetail, onGoHome, nav, siteSettings }) {
  const { addItem } = useCart();
  const [selSize,       setSelSize]       = useState(null);
  const [selImg,        setSelImg]        = useState(0);
  const [selColor,      setSelColor]      = useState(0);
  const [cartMsg,       setCartMsg]       = useState("");
  const [hovCard,       setHovCard]       = useState(null);
  const [lightbox,      setLightbox]      = useState(null); // null | 인덱스
  const [lbZoomed,      setLbZoomed]      = useState(false);
  const [lbMouse,       setLbMouse]       = useState({ x: 50, y: 50 });
  const [sizeGuideOpen, setSizeGuideOpen] = useState(false);

  const hasColors   = product.colors?.length > 0;
  const displayImgs = hasColors
    ? (product.colors[selColor]?.imgs || [null,null,null,null])
    : (product.imgs || [null,null,null,null]);

  const galleryImgs = (hasColors
    ? (product.colors[selColor]?.galleryImgs || [])
    : (product.galleryImgs || [])
  ).filter(Boolean);

  const related = allProducts.filter(p => p.id !== product.id).slice(0, 4);

  // 첫 번째 상세 이미지 너비를 측정해 나머지 이미지에 고정
  const firstGalleryRef = useRef(null);
  const [galleryWidth, setGalleryWidth] = useState(null);

  const measureFirst = useCallback(() => {
    if (firstGalleryRef.current) setGalleryWidth(firstGalleryRef.current.offsetWidth);
  }, []);

  useEffect(() => {
    setGalleryWidth(null);
    if (firstGalleryRef.current?.complete) measureFirst();
    window.addEventListener("resize", measureFirst);
    return () => window.removeEventListener("resize", measureFirst);
  }, [product.id, selColor, measureFirst]); // galleryImgs 대신 stable 값 사용

  // 유효한 이미지 인덱스 목록
  const validLbIdxs = displayImgs.map((src, i) => src ? i : null).filter(i => i !== null);
  const lbPos       = lightbox !== null ? validLbIdxs.indexOf(lightbox) : -1;
  const prevLbIdx   = lbPos > 0 ? validLbIdxs[lbPos - 1] : null;
  const nextLbIdx   = lbPos < validLbIdxs.length - 1 ? validLbIdxs[lbPos + 1] : null;

  const openLightbox = (idx) => { setLightbox(idx); setLbZoomed(false); setLbMouse({ x: 50, y: 50 }); };
  const closeLightbox = ()   => { setLightbox(null); setLbZoomed(false); };
  const goLb = (idx)         => { setLightbox(idx);  setLbZoomed(false); setLbMouse({ x: 50, y: 50 }); };

  const onImgMouseMove = (e) => {
    if (!lbZoomed) return;
    const r = e.currentTarget.getBoundingClientRect();
    setLbMouse({
      x: Math.max(0, Math.min(100, ((e.clientX - r.left) / r.width)  * 100)),
      y: Math.max(0, Math.min(100, ((e.clientY - r.top)  / r.height) * 100)),
    });
  };

  // 키보드 단축키
  useEffect(() => {
    if (lightbox === null) return;
    const onKey = (e) => {
      if (e.key === "Escape")                          closeLightbox();
      if (e.key === "ArrowLeft"  && prevLbIdx !== null) goLb(prevLbIdx);
      if (e.key === "ArrowRight" && nextLbIdx !== null) goLb(nextLbIdx);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, prevLbIdx, nextLbIdx]); // eslint-disable-line

  return (
    <>
      {/* ── 사이즈 가이드 모달 ── */}
      {sizeGuideOpen && (
        <div
          style={{ position:"fixed", inset:0, zIndex:9998, background:"rgba(0,0,0,.72)", display:"flex", alignItems:"center", justifyContent:"center", padding:16 }}
          onClick={() => setSizeGuideOpen(false)}
        >
          <div
            style={{ background:"#fff", maxWidth:720, width:"100%", maxHeight:"88vh", overflowY:"auto", position:"relative" }}
            onClick={e => e.stopPropagation()}
          >
            <button
              onClick={() => setSizeGuideOpen(false)}
              style={{ position:"absolute", top:12, right:14, background:"none", border:"none", fontSize:22, cursor:"pointer", color:"#555", zIndex:1, lineHeight:1 }}
            >✕</button>
            {product.sizeGuideImg ? (
              <img src={product.sizeGuideImg} alt="Size Guide" style={{ width:"100%", display:"block" }} />
            ) : (
              <div style={{ padding:"64px 48px", textAlign:"center", fontFamily:"'Presentation5',sans-serif", fontSize:13, color:"#999" }}>
                사이즈 가이드 이미지가 등록되지 않았습니다
              </div>
            )}
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════
          라이트박스 — AMI Paris 스타일
          흰 배경 / 좌측 썸네일 / 줌+패닝
      ══════════════════════════════════════ */}
      {lightbox !== null && (
        <div style={{ position:"fixed", inset:0, background:"#fff", zIndex:9999, display:"flex" }}>

          {/* 좌측 썸네일 목록 */}
          <div style={{
            width:80, flexShrink:0,
            borderRight:"1px solid #EBEBEB",
            display:"flex", flexDirection:"column",
            gap:4, padding:"12px 8px",
            overflowY:"auto", alignItems:"center",
          }}>
            {validLbIdxs.map(idx => (
              <div key={idx} onClick={() => goLb(idx)}
                style={{
                  width:58, flexShrink:0,
                  aspectRatio:"3/4",
                  overflow:"hidden", cursor:"pointer",
                  outline: lightbox === idx ? `1.5px solid ${C.ink}` : "1.5px solid transparent",
                  outlineOffset:1,
                  opacity: lightbox === idx ? 1 : 0.45,
                  transition:"opacity .18s, outline-color .18s",
                }}>
                <img src={displayImgs[idx]} style={{ width:"100%", height:"100%", objectFit:"cover" }} alt="" />
              </div>
            ))}
          </div>

          {/* 메인 영역 */}
          <div style={{ flex:1, display:"flex", flexDirection:"column", position:"relative" }}>

            {/* 상단 바 */}
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"14px 24px", flexShrink:0, borderBottom:"1px solid #EBEBEB" }}>
              <span style={{ fontFamily:"'Presentation5',sans-serif", fontSize:12, color:"#999", letterSpacing:.5 }}>
                {lbPos + 1} / {validLbIdxs.length}
              </span>
              <div style={{ display:"flex", alignItems:"center", gap:20 }}>
                <span style={{ fontFamily:"'Presentation5',sans-serif", fontSize:11, color:"#AAA" }}>
                  {lbZoomed ? "클릭하여 축소" : "클릭하여 확대"}
                </span>
                <button onClick={closeLightbox}
                  style={{ background:"none", border:"none", fontSize:22, cursor:"pointer", color:"#333", lineHeight:1, padding:"2px 6px", opacity:.6, transition:"opacity .15s" }}
                  onMouseEnter={e => e.currentTarget.style.opacity="1"}
                  onMouseLeave={e => e.currentTarget.style.opacity=".6"}>✕</button>
              </div>
            </div>

            {/* 이미지 + 줌+패닝 */}
            <div
              style={{
                flex:1, overflow:"hidden",
                backgroundImage: `url(${displayImgs[lightbox]})`,
                backgroundRepeat: "no-repeat",
                backgroundSize:     lbZoomed ? "100% auto" : "contain",
                backgroundPosition: lbZoomed
                  ? `center ${lbMouse.y}%`
                  : "center center",
                cursor: lbZoomed ? "zoom-out" : "zoom-in",
                transition: lbZoomed ? "none" : "background-size .25s ease",
              }}
              onClick={() => { setLbZoomed(z => !z); setLbMouse({ x: 50, y: 50 }); }}
              onMouseMove={onImgMouseMove}
              onMouseLeave={() => lbZoomed && setLbMouse({ x: 50, y: 50 })}
            />

            {/* 좌우 화살표 (축소 상태에서만) */}
            {!lbZoomed && prevLbIdx !== null && (
              <button onClick={() => goLb(prevLbIdx)}
                style={{ position:"absolute", left:20, top:"50%", transform:"translateY(-50%)",
                  background:"rgba(255,255,255,.85)", border:"1px solid #DDD",
                  color:"#333", width:44, height:44, borderRadius:"50%",
                  fontSize:20, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center",
                  boxShadow:"0 2px 8px rgba(0,0,0,.08)", transition:"background .15s" }}
                onMouseEnter={e => e.currentTarget.style.background="#fff"}
                onMouseLeave={e => e.currentTarget.style.background="rgba(255,255,255,.85)"}>‹</button>
            )}
            {!lbZoomed && nextLbIdx !== null && (
              <button onClick={() => goLb(nextLbIdx)}
                style={{ position:"absolute", right:20, top:"50%", transform:"translateY(-50%)",
                  background:"rgba(255,255,255,.85)", border:"1px solid #DDD",
                  color:"#333", width:44, height:44, borderRadius:"50%",
                  fontSize:20, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center",
                  boxShadow:"0 2px 8px rgba(0,0,0,.08)", transition:"background .15s" }}
                onMouseEnter={e => e.currentTarget.style.background="#fff"}
                onMouseLeave={e => e.currentTarget.style.background="rgba(255,255,255,.85)"}>›</button>
            )}
          </div>
        </div>
      )}

      <div style={{ maxWidth:1440, margin:"0 auto", padding:"calc(var(--header-h, 60px) + 20px) 48px 0" }}>
        {/* 브레드크럼 */}
        <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:40 }}>
          {[["Home", onGoHome], ["Shop", onGoHome]].map(([label, fn]) => (
            <span key={label} style={{ display:"contents" }}>
              <span onClick={fn} style={{ fontFamily:"'Presentation5',sans-serif", fontWeight:300, fontSize:9, letterSpacing:1, color:C.muted, cursor:"pointer", textTransform:"uppercase", transition:"color .2s" }}
                onMouseEnter={e => e.target.style.color = C.ink}
                onMouseLeave={e => e.target.style.color = C.muted}>{label}</span>
              <span style={{ color:C.border, fontSize:10 }}>›</span>
            </span>
          ))}
          <span style={{ fontFamily:"'Presentation5',sans-serif", fontWeight:300, fontSize:9, letterSpacing:1, color:C.ink, textTransform:"uppercase" }}>{product.name}</span>
        </div>

        <div className="detail-grid" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:64, alignItems:"start" }}>
          {/* 이미지 */}
          <div>
            <div style={{ marginBottom:8, aspectRatio:"3/4", position:"relative", overflow:"hidden", cursor: displayImgs[selImg] ? "zoom-in" : "default" }}
              onClick={() => displayImgs[selImg] && openLightbox(selImg)}>
              {displayImgs[selImg] ? (
                <>
                  <img src={displayImgs[selImg]} aria-hidden="true"
                    style={{ position:"absolute", inset:0, width:"100%", height:"100%", objectFit:"cover", filter:"blur(28px)", transform:"scale(1.12)", opacity:.45 }} alt="" />
                  <img src={displayImgs[selImg]}
                    style={{ position:"absolute", inset:0, width:"100%", height:"100%", objectFit:"contain", zIndex:1 }} alt={product.name} />
                </>
              ) : (
                <Img style={{ width:"100%", height:"100%" }} label="PRODUCT IMAGE" />
              )}
            </div>
            {displayImgs.filter(Boolean).length > 1 && (
              <div style={{ display:"flex", gap:4 }}>
                {displayImgs.map((src, i) => src ? (
                  <div key={i} className={`thumb-btn${selImg===i?" on":""}`} onClick={() => setSelImg(i)}
                    style={{ flex:1, aspectRatio:"1/1", position:"relative", overflow:"hidden" }}>
                    <img src={src} aria-hidden="true"
                      style={{ position:"absolute", inset:0, width:"100%", height:"100%", objectFit:"cover", filter:"blur(14px)", transform:"scale(1.12)", opacity:.45 }} alt="" />
                    <img src={src}
                      style={{ position:"absolute", inset:0, width:"100%", height:"100%", objectFit:"contain", zIndex:1 }} alt="" />
                  </div>
                ) : null)}
              </div>
            )}
          </div>

          {/* 정보 */}
          <div style={{ position:"sticky", top:80 }}>
            <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:12, flexWrap:"wrap" }}>
              <span style={{ fontFamily:"'Presentation5',sans-serif", fontWeight:300, fontSize:9, letterSpacing:1, color:C.accent, textTransform:"uppercase" }}>{product.cat}</span>
              {product.tag && (
                <span style={{ fontFamily:"'Presentation5',sans-serif", fontWeight:300, fontSize:8, letterSpacing:2, padding:"3px 8px", background:product.tag==="BEST"?C.ink:C.accent, color:"#fff" }}>{product.tag}</span>
              )}
              {product.sold_out && (
                <span style={{ fontFamily:"'Presentation5',sans-serif", fontWeight:500, fontSize:8, letterSpacing:2, padding:"3px 8px", background:"#1A1A1A", color:"#fff", textTransform:"uppercase" }}>Sold Out</span>
              )}
            </div>

            <h1 style={{ fontFamily:"'Presentation5',serif", fontWeight:400, fontSize:"clamp(28px,3.5vw,44px)", lineHeight:1.1, marginBottom:16 }}>
              {product.name}
            </h1>
            {product.discountRate > 0 ? (
              <div style={{ display:"flex", alignItems:"baseline", gap:10, marginBottom:6 }}>
                <p style={{ fontFamily:"'Presentation5',sans-serif", fontWeight:300, fontSize:20, letterSpacing:1, color:C.ink }}>
                  ₩{Math.round(product.price * (1 - product.discountRate / 100)).toLocaleString()}
                </p>
                <span style={{ fontFamily:"'Presentation5',sans-serif", fontWeight:300, fontSize:13, letterSpacing:1, color:C.muted, textDecoration:"line-through" }}>
                  ₩{product.price.toLocaleString()}
                </span>
                <span style={{ fontFamily:"'Presentation5',sans-serif", fontSize:10, fontWeight:500, color:"#C0392B", letterSpacing:1 }}>
                  {product.discountRate}% OFF
                </span>
              </div>
            ) : (
              <p style={{ fontFamily:"'Presentation5',sans-serif", fontWeight:300, fontSize:20, letterSpacing:1, color:C.ink, marginBottom:6 }}>
                ₩{product.price.toLocaleString()}
              </p>
            )}
            {(product.shippingFee ?? 0) === 0
              ? <span style={{ fontFamily:"'Pretendard',sans-serif", fontSize:10, fontWeight:600, letterSpacing:1, color:"#3D8B5C", display:"inline-block", marginBottom:12 }}>무료배송</span>
              : <span style={{ fontFamily:"'Pretendard',sans-serif", fontSize:10, color:C.muted, display:"inline-block", marginBottom:12 }}>배송비 ₩{parseInt(product.shippingFee).toLocaleString()}</span>
            }

            <ReviewSummaryBadge productId={product.id} />

            {/* ── 컬러 선택 ── */}
            {hasColors && (
              <div style={{ marginBottom:28 }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
                  <span style={{ fontFamily:"'Presentation5',sans-serif", fontWeight:300, fontSize:9, letterSpacing:1, color:C.ink, textTransform:"uppercase" }}>Color</span>
                  <span style={{ fontFamily:"'Presentation5',sans-serif", fontSize:12, fontWeight:300, color:C.muted }}>
                    {product.colors[selColor]?.name}
                  </span>
                </div>
                <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
                  {product.colors.map((col, i) => (
                    <button key={i} title={col.name}
                      onClick={() => { setSelColor(i); setSelImg(0); }}
                      style={{
                        width:30, height:30, borderRadius:"50%",
                        background: col.hex,
                        border: `1.5px solid ${C.border}`,
                        outline: selColor === i ? `2px solid ${C.ink}` : "none",
                        outlineOffset: 2,
                        cursor:"pointer",
                        transition:"outline .15s, transform .15s",
                        transform: selColor === i ? "scale(1.1)" : "scale(1)",
                      }} />
                  ))}
                </div>
              </div>
            )}

            <div style={{ height:1, background:C.border, marginBottom:28 }} />

            {/* 사이즈 */}
            <div style={{ marginBottom:28 }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:14 }}>
                <span style={{ fontFamily:"'Presentation5',sans-serif", fontWeight:300, fontSize:9, letterSpacing:1, color:C.ink, textTransform:"uppercase" }}>Size</span>
                <span className="tl" style={{ fontSize:8 }} onClick={() => setSizeGuideOpen(true)}>Size Guide →</span>
              </div>
              <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                {product.sizes.map(s => (
                  <button key={s} className={`sz-btn${selSize===s?" on":""}`} onClick={() => setSelSize(s)}>{s}</button>
                ))}
              </div>
              {!selSize && <p style={{ fontFamily:"'Presentation5',sans-serif", fontWeight:300, fontSize:9, color:C.muted, letterSpacing:1, marginTop:8 }}>사이즈를 선택해 주세요</p>}
            </div>

            {/* 구매 버튼 */}
            <div style={{ display:"flex", flexDirection:"column", gap:8, marginBottom:16 }}>
              {product.sold_out ? (
                <button className="buy-btn" disabled style={{ background:"#CCC", color:"#fff", cursor:"not-allowed", opacity:1 }}>
                  품절
                </button>
              ) : (
                <>
                  {/* 메인 액션 버튼 2개 */}
                  <div style={{ display:"flex", gap:8 }}>
                    <button className="buy-btn"
                      style={{ flex:1, background:"#F2F2F0", color:C.ink, border:"none" }}
                      onClick={() => {
                        if (!selSize) { setCartMsg("사이즈를 먼저 선택해 주세요."); setTimeout(() => setCartMsg(""), 2500); return; }
                        const colorInfo = product.colors?.[selColor];
                        const salePrice = product.discountRate > 0
                          ? Math.round(product.price * (1 - product.discountRate / 100))
                          : product.price;
                        addItem({
                          key: `${product.id}_${selSize}_${selColor}`,
                          productId: product.id,
                          productName: product.name,
                          price: salePrice,
                          shippingFee: product.shippingFee ?? 0,
                          img: displayImgs[0] || null,
                          size: selSize,
                          colorName: colorInfo?.name || null,
                          colorHex: colorInfo?.hex || null,
                        });
                        setCartMsg("장바구니에 추가되었습니다.");
                        setTimeout(() => setCartMsg(""), 2500);
                      }}
                    >장바구니 담기</button>

                    <button className="buy-btn"
                      style={{ flex:1, background:C.ink, color:C.bg, border:"none" }}
                      onClick={() => {
                        if (!selSize) { setCartMsg("사이즈를 먼저 선택해 주세요."); setTimeout(() => setCartMsg(""), 2500); return; }
                        const colorInfo = product.colors?.[selColor];
                        const salePrice = product.discountRate > 0
                          ? Math.round(product.price * (1 - product.discountRate / 100))
                          : product.price;
                        nav.goBuyNow({
                          key: `${product.id}_${selSize}_${selColor}`,
                          productId: product.id,
                          productName: product.name,
                          price: salePrice,
                          shippingFee: product.shippingFee ?? 0,
                          img: displayImgs[0] || null,
                          size: selSize,
                          quantity: 1,
                          colorName: colorInfo?.name || null,
                          colorHex: colorInfo?.hex || null,
                        });
                      }}
                    >바로 구매</button>
                  </div>

                  {/* 간편결제 섹션 */}
                  {(siteSettings?.paymentMethods?.naverPay || siteSettings?.paymentMethods?.kakaoPay || (product.naverUrlVisible && product.naverUrl)) && (
                    <div style={{ paddingTop:4 }}>
                      <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:10 }}>
                        <div style={{ flex:1, height:"1px", background:C.border }} />
                        <span style={{ fontFamily:"'Pretendard',sans-serif", fontSize:9, color:C.muted, letterSpacing:2 }}>간편결제</span>
                        <div style={{ flex:1, height:"1px", background:C.border }} />
                      </div>

                      {(siteSettings?.paymentMethods?.naverPay || siteSettings?.paymentMethods?.kakaoPay) && (
                        <div style={{ display:"flex", gap:6, marginBottom: (product.naverUrlVisible && product.naverUrl) ? 6 : 0 }}>
                          {siteSettings?.paymentMethods?.naverPay && (
                            <button
                              style={{ flex:1, padding:"13px 0", background:"#03C75A", border:"none", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", transition:"opacity .15s" }}
                              onMouseEnter={e => e.currentTarget.style.opacity='.82'}
                              onMouseLeave={e => e.currentTarget.style.opacity='1'}
                              onClick={() => {
                                if (!selSize) { setCartMsg("사이즈를 먼저 선택해 주세요."); setTimeout(() => setCartMsg(""), 2500); return; }
                                const colorInfo = product.colors?.[selColor];
                                const salePrice = product.discountRate > 0
                                  ? Math.round(product.price * (1 - product.discountRate / 100))
                                  : product.price;
                                sessionStorage.setItem('checkoutPaymentMethod', 'naverpay');
                                nav.goBuyNow({
                                  key: `${product.id}_${selSize}_${selColor}`,
                                  productId: product.id,
                                  productName: product.name,
                                  price: salePrice,
                                  shippingFee: product.shippingFee ?? 0,
                                  img: displayImgs[0] || null,
                                  size: selSize,
                                  quantity: 1,
                                  colorName: colorInfo?.name || null,
                                  colorHex: colorInfo?.hex || null,
                                });
                              }}
                            ><NPayLogo /></button>
                          )}
                          {siteSettings?.paymentMethods?.kakaoPay && (
                            <button
                              style={{ flex:1, padding:"13px 0", background:"#FEE500", border:"none", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", transition:"opacity .15s" }}
                              onMouseEnter={e => e.currentTarget.style.opacity='.82'}
                              onMouseLeave={e => e.currentTarget.style.opacity='1'}
                              onClick={() => {
                                if (!selSize) { setCartMsg("사이즈를 먼저 선택해 주세요."); setTimeout(() => setCartMsg(""), 2500); return; }
                                alert('카카오페이 연동 준비 중입니다.');
                              }}
                            ><KakaoPayLogo /></button>
                          )}
                        </div>
                      )}

                      {product.naverUrlVisible && product.naverUrl && (
                        <a href={product.naverUrl} target="_blank" rel="noreferrer"
                          style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:5, padding:"9px 0", textDecoration:"none", fontFamily:"'Pretendard',sans-serif", fontSize:10, color:C.muted, letterSpacing:.3, transition:"color .15s" }}
                          onMouseEnter={e => e.currentTarget.style.color='#03C75A'}
                          onMouseLeave={e => e.currentTarget.style.color=C.muted}
                        >
                          <NaverSquareIcon />
                          스마트스토어에서 구매
                          <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M2 5h6M5.5 2.5 8 5l-2.5 2.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/></svg>
                        </a>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>

            {cartMsg && (
              <p style={{ fontFamily:"'Pretendard',sans-serif", fontSize:11, letterSpacing:1, color:cartMsg.includes("추가") ? "#3D8B5C" : "#C94040", textAlign:"center", marginBottom:12, animation:"fadeIn .2s" }}>
                {cartMsg}
              </p>
            )}

            {/* 결제 뱃지 */}
            <div style={{ display:"flex", alignItems:"center", gap:8, padding:"12px 16px", background:C.bg2, border:`1px solid ${C.border}`, marginBottom:32 }}>
              <span style={{ fontFamily:"'Presentation5',sans-serif", fontWeight:300, fontSize:9, letterSpacing:1, color:C.muted }}>
                {(product.shippingFee ?? 0) === 0 ? "무료배송" : `배송비 ₩${parseInt(product.shippingFee).toLocaleString()}`} · 카드 · 무통장 입금 · 간편결제
              </span>
            </div>

            <div style={{ height:1, background:C.border, marginBottom:28 }} />

            {/* 제품 정보 */}
            {[
              { title:"Product Details",    body: product.desc },
              { title:"Fabric & Care",      body: product.fabricCare || `소재: ${product.fabric}\n원산지: ${product.origin}\n\n세탁 시 30°C 이하 찬물 세탁, 드라이 클리닝 불가. 표백제 사용 금지.` },
              { title:"Fit & Sizing",       body: product.fitSizing || `핏: ${product.fit}\n\n모델 신장 182cm, M 사이즈 착용 기준.\n넉넉한 핏을 원하시면 한 사이즈 업을 권장합니다.` },
              { title:"Shipping & Returns", body: product.shippingReturns || "무료 배송 기준: ₩50,000 이상\n배송 기간: 2–3 영업일\n교환 / 반품: 수령 후 7일 이내 가능" },
            ].map(({ title, body }) => (
              <div key={title} style={{ marginBottom:24, paddingBottom:24, borderBottom:`1px solid ${C.border}` }}>
                <p style={{ fontFamily:"'Presentation5',sans-serif", fontWeight:300, fontSize:10, letterSpacing:1, color:C.ink, textTransform:"uppercase", marginBottom:10 }}>{title}</p>
                <p style={{ fontFamily:"'Presentation5',sans-serif", fontSize:13, fontWeight:300, color:C.muted, lineHeight:2, whiteSpace:"pre-line" }}>{body}</p>
              </div>
            ))}
          </div>
        </div>

        {/* 제품 상세 이미지 */}
        {galleryImgs.length > 0 && (
          <section style={{ marginTop:96, marginBottom:96, display:"flex", flexDirection:"column", alignItems:"center", gap:30 }}>
            {galleryImgs.map((img, i) => (
              <img key={i}
                ref={i === 0 ? firstGalleryRef : null}
                src={img}
                onLoad={i === 0 ? measureFirst : undefined}
                style={i === 0
                  ? { width:"auto", height:"auto", maxHeight:"100vh", maxWidth:"100%", display:"block" }
                  : { width: galleryWidth ? `${galleryWidth}px` : "auto", height:"auto", maxWidth:"100%", display:"block" }
                }
                alt=""
              />
            ))}
          </section>
        )}

        {/* 리뷰 */}
        <ProductReviews productId={product.id} />

        {/* 추천 상품 */}
        <div style={{ marginTop:96, paddingTop:64, borderTop:`1px solid ${C.border}` }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:36 }}>
            <h3 style={{ fontFamily:"'DM Serif Display',serif", fontWeight:400, fontSize:28 }}>You May Also Like</h3>
            <span className="tl" onClick={onGoHome}>View All →</span>
          </div>
          <div className="prod-grid" style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:2 }}>
            {related.map(p => (
              <div key={p.id} className="prod-card"
                onMouseEnter={() => setHovCard(p.id)}
                onMouseLeave={() => setHovCard(null)}
                onClick={() => { onGoDetail(p.id); setSelSize(null); setSelImg(0); setSelColor(0); }}>
                <div className="pimg" style={{ aspectRatio:"3/4" }}>
                  <div className="pimg-inner">
                    {p.imgs?.[0]
                      ? <img src={p.imgs[0]} style={{ width:"100%", height:"100%", objectFit:"cover" }} alt={p.name} />
                      : <Img style={{ width:"100%", height:"100%" }} />
                    }
                  </div>
                  {p.tag && (
                    <div style={{ position:"absolute", top:12, left:12, background:p.tag==="BEST"?C.ink:C.accent, padding:"4px 10px" }}>
                      <span style={{ fontFamily:"'Presentation5',sans-serif", fontWeight:300, fontSize:8, letterSpacing:1, color:"#fff" }}>{p.tag}</span>
                    </div>
                  )}
                  <div style={{ position:"absolute", inset:0, border:hovCard===p.id?`2px solid ${C.accent}`:"2px solid transparent", transition:"border-color .25s", pointerEvents:"none" }} />
                </div>
                <div style={{ paddingTop:10 }}>
                  <p style={{ fontFamily:"'Presentation5',sans-serif", fontSize:13, color:hovCard===p.id?C.hover:C.ink, fontWeight:hovCard===p.id?700:400, transition:"color .25s", marginBottom:2 }}>{p.name}</p>
                  {p.discountRate > 0 ? (
                    <div style={{ display:"flex", alignItems:"baseline", gap:6 }}>
                      <span style={{ fontFamily:"'Presentation5',sans-serif", fontWeight:300, fontSize:12, color:C.ink, letterSpacing:1 }}>₩{Math.round(p.price * (1 - p.discountRate / 100)).toLocaleString()}</span>
                      <span style={{ fontFamily:"'Presentation5',sans-serif", fontWeight:300, fontSize:10, color:C.muted, letterSpacing:1, textDecoration:"line-through" }}>₩{p.price.toLocaleString()}</span>
                      <span style={{ fontFamily:"'Presentation5',sans-serif", fontSize:9, fontWeight:500, color:"#C0392B" }}>{p.discountRate}%</span>
                    </div>
                  ) : (
                    <p style={{ fontFamily:"'Presentation5',sans-serif", fontWeight:300, fontSize:12, color:C.ink, letterSpacing:1 }}>₩{p.price.toLocaleString()}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <Footer nav={nav} variant="full" />
    </>
  );
}

function NaverSquareIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <rect width="16" height="16" rx="2.5" fill="#03C75A"/>
      <path d="M4 12V4h2.2l3.6 5.4V4H12v8H9.8L6.2 6.6V12H4z" fill="white"/>
    </svg>
  );
}

function NPayLogo() {
  return (
    <svg width="56" height="20" viewBox="0 0 56 20" fill="none">
      <rect width="20" height="20" rx="3.5" fill="white"/>
      <path d="M4.5 15V5h2.4l4 6V5H13.1v10H10.7l-4-6V15H4.5z" fill="#03C75A"/>
      <text x="24" y="14.5" fill="white" fontFamily="Arial,sans-serif" fontSize="12" fontWeight="700">pay</text>
    </svg>
  );
}

function KakaoPayLogo() {
  return (
    <svg width="72" height="20" viewBox="0 0 72 20" fill="none">
      <text x="0" y="14.5" fill="#3C1E1E" fontFamily="Arial,sans-serif" fontSize="11" fontWeight="700">kakao</text>
      <text x="40" y="14.5" fill="#3C1E1E" fontFamily="Arial,sans-serif" fontSize="12" fontWeight="700">pay</text>
    </svg>
  );
}
